import React, { useState, useEffect } from "react";
import { Card, Descriptions, Select, Button, Space, Alert, Typography, Divider, Modal, Upload, message } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { DeleteButton } from "@refinedev/antd";
import { Sourcing, SourcingStatus, Attachment } from "../interface";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { DOCUMENT_TEMPLATES_QUERY, GENERATE_AWARD_LETTER_MUTATION, GET_BIDS_BY_SOURCING_QUERY, DELETE_AWARD_LETTER_MUTATION, CREATE_PURCHASE_ORDER_FROM_SOURCING_MUTATION } from "../queries";
import { notification } from "antd";

const { Title, Text } = Typography;

const deriveFileApiBase = () => {
  return import.meta.env.VITE_API_URL
};

const fileUploadUrl = `${deriveFileApiBase()}/file-storage/upload`;

// Status order for index-based logic
const SOURCING_STATUS_ORDER = [
  SourcingStatus.DRAFT,
  SourcingStatus.SUBMITTED,
  SourcingStatus.APPROVED,
  SourcingStatus.PUBLISHED,
  SourcingStatus.CLOSED,
  SourcingStatus.REJECTED,
  SourcingStatus.RESULT_SUBMITTED,
  SourcingStatus.AWARD_APPROVED,
  SourcingStatus.PARTIALLY_AWARDED,
  SourcingStatus.AWARDED,
];

interface AwardTabProps {
  record: Sourcing;
  onRefetch?: () => void;
}

interface BidData {
  _id: string;
  supplierId: string;
  companyName: string;
  items: Array<{
    itemName: string;
    quantity: number;
    unitPrice: number;
    total: number;
    deliveryTime?: string;
  }>;
  compliance?: boolean;
  decision?: string;
  evaluationRemarks?: string;
  evaluationResults?: Array<{
    criteriaId: string;
    value: number;
    remarks?: string;
  }>;
}

interface DocumentTemplate {
  id: string;
  documentName: string;
  category: string;
  tag: string;
  url: string;
}

export const AwardTab: React.FC<AwardTabProps> = ({ record, onRefetch }) => {
  const [bids, setBids] = useState<BidData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [acceptDialogVisible, setAcceptDialogVisible] = useState(false);
  const [acceptanceLetter, setAcceptanceLetter] = useState<Attachment | null>(null);
  const [accepting, setAccepting] = useState(false);

  // Calculate status index for simplified logic
  const statusIndex = SOURCING_STATUS_ORDER.indexOf(record.status);

  // Query for document templates
  const { data: templatesData, isLoading: templatesLoading } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: DOCUMENT_TEMPLATES_QUERY,
    },
  });

  // Query for bids - only enable when status is AWARDED
  const { refetch: refetchBids } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_BIDS_BY_SOURCING_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: !!record?.id && statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.AWARD_APPROVED),
    },
  });

  // Mutation for generating award letter
  const { mutate: generateAwardLetter } = useCustomMutation();

  // Mutation for accepting award
  const { mutate: acceptAward } = useCustomMutation();

  useEffect(() => {
    if (statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.AWARD_APPROVED) && record?.id) {
      loadBidsData();
    }
  }, [statusIndex, record?.id]);

  const loadBidsData = async () => {
    if (!record?.id || !refetchBids) return;

    setLoading(true);
    try {
      const response = await refetchBids();
      const bidsData = (response as any)?.data?.data?.getBidsBySourcing || [];
      setBids(bidsData);
    } catch (error) {
      console.error('Error loading bids data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (statusIndex < SOURCING_STATUS_ORDER.indexOf(SourcingStatus.AWARD_APPROVED)) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Award documents will be available after the sourcing is awarded.
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading award data...
        </div>
      </Card>
    );
  }

  // Find winners: use itemAwards if available, else fall back to bid-level decision
  const itemAwards = (record as any).itemAwards || [];
  let winnerGroups: { supplierId: string; supplierName: string; total: number }[] = [];

  if (itemAwards.length > 0 && itemAwards.some((a: any) => a.decision === 'Winner')) {
    const supplierMap = new Map<string, { supplierId: string; supplierName: string; total: number }>();
    for (const award of itemAwards) {
      if (award.decision === 'Winner') {
        const existing = supplierMap.get(award.supplierId);
        if (existing) {
          existing.total += award.totalPrice;
        } else {
          supplierMap.set(award.supplierId, { supplierId: award.supplierId, supplierName: award.supplierName, total: award.totalPrice });
        }
      }
    }
    winnerGroups = Array.from(supplierMap.values());
  } else {
    const winnerBid = bids.find(bid => bid.decision === 'Winner');
    if (winnerBid) {
      const total = winnerBid.items.reduce((sum: number, item: any) => sum + (item.total || 0), 0);
      winnerGroups = [{ supplierId: winnerBid.supplierId, supplierName: winnerBid.companyName, total }];
    }
  }

  if (winnerGroups.length === 0 && itemAwards.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          No winner has been selected for this sourcing event.
        </div>
      </Card>
    );
  }

  // Filter templates for award documents
  const awardTemplates = (templatesData?.data?.documentTemplates?.nodes || []).filter(
    (template: DocumentTemplate) => template.category === 'AWARD' || template.tag === 'AWARD' ||  template.documentName?.toLowerCase()?.includes("award")
  );

  const handleGenerateDocument = async (supplierId?: string) => {
    if (!selectedTemplate) {
      notification.error({
        message: 'Template Required',
        description: 'Please select a document template first.',
      });
      return;
    }

    setGenerating(true);
    try {
      generateAwardLetter(
        {
          url: "",
          method: "post",
          values: {
            sourcingId: record.id,
            documentTemplateId: selectedTemplate,
            supplierId: supplierId || undefined,
          },
          meta: { gqlMutation: GENERATE_AWARD_LETTER_MUTATION },
        },
        {
          onSuccess: (response: any) => {
            notification.success({
              message: 'Award Letter Generated',
              description: 'Award letter has been generated.',
            });
            if (onRefetch) onRefetch();
          },
          onError: (error: any) => {
            notification.error({
              message: 'Generation Failed',
              description: error.message || 'Failed to generate award letter.',
            });
          },
        },
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleCreatePurchaseOrder = (supplierId?: string) => {
    setAccepting(true);
    try {
      acceptAward(
        {
          url: "",
          method: "post",
          values: {
            input: {
              sourcingId: record.id,
              supplierId: supplierId || undefined,
            },
          },
          meta: { gqlMutation: CREATE_PURCHASE_ORDER_FROM_SOURCING_MUTATION },
        },
        {
          onSuccess: (response: any) => {
            notification.success({
              message: 'Purchase Order Created',
              description: 'The purchase order has been created successfully.',
            });
            setAcceptDialogVisible(false);
            setAcceptanceLetter(null);
            if (onRefetch) onRefetch();
          },
          onError: (error: any) => {
            notification.error({
              message: 'Purchase Order Creation Failed',
              description: error.message || 'Failed to create purchase order from sourcing.',
            });
          },
        },
      );
    } catch (error) {
      notification.error({
        message: 'Purchase Order Creation Failed',
        description: 'An unexpected error occurred.',
      });
    } finally {
      setAccepting(false);
    }
  };

  const acceptanceUploadProps = {
    name: "file",
    multiple: false,
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      try {
        const fd = new FormData();
        fd.append("file", file as Blob);
        const resp = await fetch(fileUploadUrl, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Upload failed: ${resp.status}`);
        }
        const json = await resp.json();
        const fileData = json?.data;
        if (!fileData || !fileData.fileId) {
          throw new Error("Upload response missing file data or fileId");
        }
        // Create attachment data
        const attachmentData: Attachment = {
          fileId: fileData.fileId,
          originalName: fileData.originalName,
          mimeType: fileData.mimeType,
          size: fileData.size,
          url: fileData.url,
          category: "LETTER",
        };
        setAcceptanceLetter(attachmentData);
        onSuccess?.(json, file);
        message.success("Acceptance letter uploaded successfully");
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || "Upload failed");
        onError?.(e);
      }
    },
    showUploadList: false,
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* Template Selection - global */}
      <Card title="Document Template">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Select an award document template:</Text>
          <Select
            placeholder="Choose a template"
            style={{ width: '100%', maxWidth: 400 }}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
            loading={templatesLoading}
          >
            {awardTemplates.map((template: DocumentTemplate) => (
              <Select.Option key={template.id} value={template.id}>
                {template.documentName}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Card>

      {/* Per-Supplier Award Groups */}
      {winnerGroups.length > 0 ? winnerGroups.map((group, idx) => {
        const supplierAwards = itemAwards.filter((a: any) => a.supplierId === group.supplierId && a.decision === 'Winner');
        const supplierAwardLetters = (record.awardLetters || []).filter((l: any) => l.supplierId === group.supplierId);

        return (
          <Card 
            key={group.supplierId}
            title={winnerGroups.length > 1 ? `Award Group #${idx + 1} — ${group.supplierName}` : `Award — ${group.supplierName}`}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Supplier">{group.supplierName}</Descriptions.Item>
                <Descriptions.Item label="Total Amount">{record.currency || 'ETB'} {group.total.toLocaleString()}</Descriptions.Item>
                <Descriptions.Item label="Items Awarded">{supplierAwards.length}</Descriptions.Item>
                <Descriptions.Item label="Reference">AWARD-{record.sourcingNo}</Descriptions.Item>
              </Descriptions>

              {supplierAwards.length > 0 && (
                <div>
                  <Text strong>Awarded Items:</Text>
                  <ul>
                    {supplierAwards.map((a: any) => {
                      const item = record.items.find(i => i.id === a.sourcingItemId);
                      return (
                        <li key={a.sourcingItemId}>
                          {item?.name || a.sourcingItemId}: {a.quantity} @ {a.unitPrice.toLocaleString()}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Award Letters for this supplier */}
              {supplierAwardLetters.length > 0 && supplierAwardLetters.map((letter: any, li: number) => (
                <Card key={letter.fileId || li} size="small" title={`Award Letter ${li + 1}`}>
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="File">{letter.originalName}</Descriptions.Item>
                    <Descriptions.Item label="Size">{(letter.size / 1024).toFixed(2)} KB</Descriptions.Item>
                  </Descriptions>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    href={letter.url}
                    target="_blank"
                    size="small"
                  >
                    Download
                  </Button>
                </Card>
              ))}

              {/* Generate Award Letter for this supplier */}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleGenerateDocument(group.supplierId)}
                disabled={!selectedTemplate || generating}
                loading={generating}
              >
                Generate Award Letter for {group.supplierName}
              </Button>

              {/* Create PO for this supplier */}
              {record.status !== SourcingStatus.AWARDED && (
                <Button
                  type="default"
                  onClick={() => handleCreatePurchaseOrder(group.supplierId)}
                  loading={accepting}
                >
                  Create Purchase Order for {group.supplierName}
                </Button>
              )}
            </Space>
          </Card>
        );
      }) : (
        /* Backward compat: single winner display */
        <>
          {(() => {
            const winnerBid = bids.find(bid => bid.decision === 'Winner');
            if (!winnerBid) return null;
            const winnerTotal = winnerBid.items.reduce((sum: number, item: any) => sum + (item.total || 0), 0);
            return (
              <Card title="Award Information">
                <Descriptions column={2} bordered>
                  <Descriptions.Item label="Date">{new Date().toLocaleDateString()}</Descriptions.Item>
                  <Descriptions.Item label="Reference">AWARD-{record.sourcingNo}</Descriptions.Item>
                  <Descriptions.Item label="Winner Supplier">{winnerBid.companyName}</Descriptions.Item>
                  <Descriptions.Item label="Sourcing Number">{record.sourcingNo}</Descriptions.Item>
                  <Descriptions.Item label="Total Amount" span={2}>{record.currency || "ETB"} {winnerTotal.toLocaleString()}</Descriptions.Item>
                </Descriptions>
              </Card>
            );
          })()}

          {record.awardLetter && (
            <Card title="Generated Award Letter">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="File Name">{record.awardLetter.originalName}</Descriptions.Item>
                  <Descriptions.Item label="Size">{(record.awardLetter.size / 1024).toFixed(2)} KB</Descriptions.Item>
                </Descriptions>
                <Button type="primary" icon={<DownloadOutlined />} href={record.awardLetter.url} target="_blank" size="large">
                  Download Award Letter
                </Button>
              </Space>
            </Card>
          )}

          {!record.awardLetter && (
            <Card>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleGenerateDocument()}
                disabled={!selectedTemplate || generating}
                loading={generating}
                size="large"
              >
                Generate Award Document
              </Button>
            </Card>
          )}

          {record.status !== SourcingStatus.AWARDED && (
            <Card>
              <Button
                type="primary"
                size="large"
                onClick={() => setAcceptDialogVisible(true)}
                style={{ height: '50px', fontSize: '16px' }}
              >
                Create Purchase Order
              </Button>
            </Card>
          )}
        </>
      )}

      {/* Acceptance Dialog */}
      <Modal
        title="Create Purchase Order"
        open={acceptDialogVisible}
        onOk={() => handleCreatePurchaseOrder()}
        onCancel={() => {
          setAcceptDialogVisible(false);
          setAcceptanceLetter(null);
        }}
        okText="Create Purchase Order"
        cancelText="Cancel"
        confirmLoading={accepting}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Create Purchase Order. Confirm that the sourcing will be awarded. Draft PO will be created, notification to the sourcing requester will be sent.
          </Text>
          <Divider />
          <Text strong>Upload Acceptance Letter from Supplier:</Text>
          <Upload {...acceptanceUploadProps}>
            <Button icon={<UploadOutlined />}>
              Upload Acceptance Letter
            </Button>
          </Upload>
          {acceptanceLetter && (
            <div style={{ padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
              <Text>Uploaded: {acceptanceLetter.originalName}</Text>
            </div>
          )}
        </Space>
      </Modal>
    </Space>
  );
};